import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import initSqlJs, { Database, SqlJsStatic } from 'sql.js';
import { Flow, normalizeSources, mergeSources } from '../obj/Flow';
import { FlowRun } from '../obj/FlowRun';
import { Connection } from '../obj/Connection';
import { MsalHelperService } from './msal-helper.service';
import { ConfigurationItem, UserItem, mergeEmails } from './storage-types';
import { EnvScanSource, RunScanQueueOptions, StorageBackend, StorageMode, StoragePutOptions, StoredEnvironment, StoreStats } from './storage-backend';
import { FLOW_STUDIO_SQLITE_DB_FILE, locateSqliteWasm } from './storage-sqlite.constants';
import { adminTrace } from './debug-host.util';

type SqlValue = string | number | null;

type Row = Record<string, SqlValue>;

@Injectable({
  providedIn: 'root'
})
export class StorageOpfsSqliteService implements StorageBackend {
  private sqlite: SqlJsStatic | null = null;
  private db: Database | null = null;
  private dbInitPromise: Promise<void> | null = null;
  private fileHandle: FileSystemFileHandle | null = null;
  private userOid = '';

  private flowsSubject = new BehaviorSubject<Flow[]>([]);
  private flowsAdminSubject = new BehaviorSubject<Flow[]>([]);
  private runsSubject = new BehaviorSubject<FlowRun[]>([]);
  private usersSubject = new BehaviorSubject<UserItem[]>([]);
  private connectionsSubject = new BehaviorSubject<Connection[]>([]);

  constructor(private msalHelperService: MsalHelperService) {
    this.userOid = msalHelperService.getUser()?.['oid'] || '';
    this.msalHelperService.user$.subscribe((user) => {
      const nextOid = (user as any)?.oid || '';
      // MSAL re-emits user$ on every interaction-status change and silent token
      // refresh. Skip the (expensive) full subject refresh unless the actual
      // logged-in identity changed — otherwise we re-read every row from SQLite,
      // re-parse hundreds of JSON blobs, and force CD across every grid that
      // listens to flowsSubject (the /flows page can pin the main thread for
      // tens of seconds while that loops on a big tenant).
      if (nextOid === this.userOid) {
        return;
      }
      this.userOid = nextOid;
      this.scheduleRefreshSubjects();
    });
  }

  /** setTimeout(0) coalescer for refreshSubjects — multiple calls in one tick collapse to one. */
  private refreshSubjectsScheduled = false;
  private scheduleRefreshSubjects(): void {
    if (this.refreshSubjectsScheduled) return;
    this.refreshSubjectsScheduled = true;
    setTimeout(() => {
      this.refreshSubjectsScheduled = false;
      this.refreshSubjects().catch((error) => {
        console.warn('[StorageOpfsSqliteService] failed to refresh subjects', error);
      });
    }, 0);
  }

  getMode(): StorageMode {
    return 'main-opfs';
  }

  async probe(): Promise<{ ok: boolean; error?: string }> {
    try {
      await this.ensureReady();
      return { ok: true };
    } catch (error: any) {
      if (!this.db) {
        this.dbInitPromise = null;
      }
      return { ok: false, error: String(error?.message ?? error) };
    }
  }

  async getConfig(key: string): Promise<ConfigurationItem> {
    await this.ensureReady();
    const row = this.queryOne('SELECT value FROM configs WHERE user = ? AND key = ?', [this.userOid, key]);
    if (!row) {
      return { key, value: null };
    }
    return {
      key,
      value: this.deserializeJson(row.value as string)
    };
  }

  async getConfigs(keys: string[]): Promise<ConfigurationItem[]> {
    return Promise.all((keys || []).map((key) => this.getConfig(key)));
  }

  async putConfig(key: string, item: ConfigurationItem): Promise<any> {
    await this.ensureReady();
    this.exec(
      'INSERT INTO configs (key, user, value) VALUES (?, ?, ?) ON CONFLICT(user, key) DO UPDATE SET value=excluded.value',
      [key, this.userOid, JSON.stringify(item?.value ?? null)]
    );
    await this.persist();
    return true;
  }

  async getFlows(): Promise<Flow[]> {
    await this.ensureReady();
    // No source filter — the user-scope scan path doesn't reliably add a
    // 'my'/'shared' tag today, so a flow that's been admin-scanned ends up
    // tagged just 'admin' even though the user has it via user scope.
    // Filtering on `sources <> 'admin'` would hide most of those rows.
    // Revisit when the non-admin scan paths tag with a positive source.
    const rows = this.queryAll('SELECT json FROM flows WHERE user = ?', [this.userOid]);
    const flows = rows.map((row) => this.toFlow(row.json as string));
    this.flowsSubject.next(flows);
    return flows;
  }

  async getFlowSummaries(environment?: string): Promise<Flow[]> {
    console.log('[opfs] getFlowSummaries start, env=', environment);
    await this.ensureReady();
    console.log('[opfs] getFlowSummaries ensureReady done');
    const rows = environment
      ? this.queryAll('SELECT key, json, environment, displayName, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError FROM flows WHERE user = ? AND environment = ?', [this.userOid, environment])
      : this.queryAll('SELECT key, json, environment, displayName, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError FROM flows WHERE user = ?', [this.userOid]);
    console.log('[opfs] query done, rows=', rows.length);
    console.time('[opfs] toFlowSummary map');
    const result = rows.map((row) => this.toFlowSummary(row));
    console.timeEnd('[opfs] toFlowSummary map');
    console.log('[opfs] getFlowSummaries done');
    return result;
  }

  async getFlowsAdmin(): Promise<Flow[]> {
    await this.ensureReady();
    // Single `flows` table; admin is derived from the row's sources tag list.
    const rows = this.queryAll(
      "SELECT json FROM flows WHERE user = ? AND sources LIKE '%admin%'",
      [this.userOid]
    );
    const flows = rows.map((row) => this.toFlow(row.json as string));
    // [admin-trace] Confirm the OPFS subject is actually being pushed to with
    // admin flows. If this fires with N>0 but the component never sees it,
    // there's a subscription/change-detection problem upstream.
    const sample = flows.slice(0, 3).map((f: any) => ({
      flowName: f?.flowName,
      environment: f?.environment,
      sources: f?.sources,
    }));
    adminTrace(`[admin-trace] opfs.getFlowsAdmin pushing rows=${rows.length} flows=${flows.length} sample=${JSON.stringify(sample)}`);
    this.flowsAdminSubject.next(flows);
    return flows;
  }

  getFlows$(): Observable<Flow[]> {
    this.getFlows().catch((error) => {
      console.warn('[StorageOpfsSqliteService] getFlows$ initial refresh failed', error);
    });
    return this.flowsSubject.asObservable();
  }

  getFlowsAdmin$(): Observable<Flow[]> {
    this.getFlowsAdmin().catch((error) => {
      console.warn('[StorageOpfsSqliteService] getFlowsAdmin$ initial refresh failed', error);
    });
    return this.flowsAdminSubject.asObservable();
  }

  async putFlow(flow: Flow, admin = false, options: StoragePutOptions = {}): Promise<any> {
    await this.putFlows([flow], admin, options);
    return true;
  }

  async putFlows(flows: Flow[], admin = false, options: StoragePutOptions = {}): Promise<any> {
    await this.ensureReady();
    flows = flows?.filter((flow) => flow.displayName) || [];
    if (!flows.length) {
      return true;
    }

    for (const flow of flows) {
      const key = `${flow.environment}/${flow.flowName}`;
      const displayName = flow.displayName || '';
      const environment = flow.environment || '';

      // Union sources with whatever is already stored so a flow visible from
      // both /api/flows (my/shared) and the admin scan retains BOTH origin tags
      // in a single row. The `admin` boolean parameter is treated as another
      // source contribution for back-compat with callers that haven't yet set
      // flow.sources themselves.
      const incomingSources = mergeSources(flow.sources, admin ? ['admin'] : []);
      const existing = this.queryOne('SELECT json, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
      const existingStored = existing ? (this.deserializeJson(existing.json as string) || {}) : {};
      const existingSources = existing
        ? normalizeSources(existingStored.sources)
        : [];
      const merged = mergeSources(existingSources, incomingSources);

      // Mutate the in-memory flow so any subjects pushed downstream see the union.
      flow.sources = merged;

      const stored = this.toStoredFlow(flow);
      stored.sources = merged;
      this.applyExistingRunScanQueue(stored, existing, existingStored);
      const json = JSON.stringify(stored);
      // Store sources as a JSON array string (e.g. '["my","admin"]') so
      // it round-trips with `JSON.parse` and is unambiguously typed. The
      // older CSV format (e.g. 'my,admin') is still readable by the
      // filter SQL below — old rows migrate lazily as they get re-written.
      const sourcesJson = JSON.stringify(merged.map(s => s.toLowerCase()));

      this.exec(
        `INSERT INTO flows (key, json, environment, displayName, user, sources, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
         ON CONFLICT(key) DO UPDATE SET
           json=excluded.json,
           environment=excluded.environment,
           displayName=excluded.displayName,
           user=excluded.user,
           sources=excluded.sources,
           runScanQueuedAt=excluded.runScanQueuedAt,
           runScanReason=excluded.runScanReason,
           runScanAttempts=excluded.runScanAttempts,
           runScanLastError=excluded.runScanLastError`,
        [key, json, environment, displayName, this.userOid, sourcesJson, stored.runScanQueuedAt ?? null, stored.runScanReason ?? null, stored.runScanAttempts ?? 0, stored.runScanLastError ?? null]
      );
    }

    if (options.persist !== false) {
      await this.persist();
    }
    await this.refreshFlowSubjects();
    return true;
  }

  async flush(): Promise<void> {
    await this.ensureReady();
    await this.persist();
  }

  /** Diagnostic — every config row. */
  async getAllConfigs(): Promise<{ key: string; value: any }[]> {
    await this.ensureReady();
    const rows = this.queryAll('SELECT key, value FROM configs WHERE user = ? ORDER BY key', [this.userOid]);
    return rows.map((row) => ({
      key: String(row.key ?? ''),
      value: this.deserializeJson(row.value as string),
    }));
  }

  /** Diagnostic — every user_aliases row. */
  async getAllUserAliases(): Promise<{ email: string; user_key: string }[]> {
    await this.ensureReady();
    const rows = this.queryAll('SELECT email, user_key FROM user_aliases WHERE user = ? ORDER BY email', [this.userOid]);
    return rows.map((row) => ({
      email: String(row.email ?? ''),
      user_key: String(row.user_key ?? ''),
    }));
  }

  /** Diagnostic — counts rows in every known table + reports the OPFS file size. */
  async getStoreStats(): Promise<StoreStats> {
    await this.ensureReady();
    const tableNames = ['configs', 'flows', 'runs', 'users', 'user_aliases', 'environments', 'connections'];
    const tables: Record<string, number> = {};
    for (const name of tableNames) {
      try {
        const row = this.queryOne(`SELECT COUNT(*) AS c FROM ${name}`, []);
        tables[name] = row ? Number(row.c ?? 0) : 0;
      } catch {
        tables[name] = -1; // table missing
      }
    }
    let fileSizeBytes: number | null = null;
    try {
      if (this.fileHandle) {
        const file = await this.fileHandle.getFile();
        fileSizeBytes = file.size;
      }
    } catch { /* ignore */ }
    return { fileSizeBytes, tables };
  }

  /** Wipe the OPFS-backed SQLite database and reinitialise the schema. */
  async nukeStore(): Promise<void> {
    await this.ensureReady();
    if (this.db) {
      try { this.db.close(); } catch { /* ignore */ }
      this.db = null;
    }
    // Best-effort delete of the underlying file. If unsupported, fall back to truncating.
    try {
      const root = await navigator.storage.getDirectory();
      await root.removeEntry(FLOW_STUDIO_SQLITE_DB_FILE).catch(() => undefined);
    } catch { /* ignore */ }
    this.fileHandle = null;
    this.dbInitPromise = null;
    // Reset the cached subjects so consumers see empty state immediately.
    this.flowsSubject.next([]);
    this.flowsAdminSubject.next([]);
    this.runsSubject.next([]);
    this.usersSubject.next([]);
    // Reinitialise so subsequent calls find a fresh DB.
    await this.ensureReady();
  }

  async removeFlows(flows: Flow[], admin = false): Promise<any> {
    await this.ensureReady();
    if (!flows?.length) {
      return true;
    }

    for (const flow of flows) {
      const key = `${flow.environment}/${flow.flowName}`;
      if (admin) {
        // Admin removal: drop only the 'admin' source tag. If the flow had other
        // origins (my/shared), keep the row — only the admin view should forget it.
        const existing = this.queryOne('SELECT json FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
        if (!existing) continue;
        const stored = this.deserializeJson(existing.json as string) || {};
        const remaining = normalizeSources(stored.sources).filter((s) => s.toLowerCase() !== 'admin');
        if (remaining.length === 0) {
          this.exec('DELETE FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
        } else {
          stored.sources = remaining;
          this.exec(
            'UPDATE flows SET json = ?, sources = ? WHERE key = ? AND user = ?',
            [JSON.stringify(stored), remaining.join(',').toLowerCase(), key, this.userOid]
          );
        }
      } else {
        this.exec('DELETE FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
      }
    }

    await this.persist();
    await this.refreshFlowSubjects();
    return true;
  }

  async markRunScanQueued(environment: string, flowNames: string[], options: RunScanQueueOptions = {}): Promise<any> {
    await this.ensureReady();
    const names = (flowNames || []).filter(Boolean);
    if (!environment || !names.length) return true;
    const now = Date.now();
    for (const flowName of names) {
      const key = `${environment}/${flowName}`;
      const existing = this.queryOne('SELECT json, runScanAttempts FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
      if (!existing) continue;
      const stored = this.deserializeJson(existing.json as string) || {};
      const attempts = Number(existing.runScanAttempts ?? stored.runScanAttempts ?? 0) + 1;
      stored.runScanQueuedAt = now;
      stored.runScanReason = options.reason || 'batch-failed';
      stored.runScanAttempts = attempts;
      stored.runScanLastError = options.error || null;
      this.exec(
        `UPDATE flows
            SET json = ?, runScanQueuedAt = ?, runScanReason = ?, runScanAttempts = ?, runScanLastError = ?
          WHERE key = ? AND user = ?`,
        [JSON.stringify(stored), now, stored.runScanReason, attempts, stored.runScanLastError, key, this.userOid]
      );
    }
    await this.persist();
    await this.refreshFlowSubjects();
    return true;
  }

  async clearRunScanQueued(environment: string, flowNames: string[]): Promise<any> {
    await this.ensureReady();
    const names = (flowNames || []).filter(Boolean);
    if (!environment || !names.length) return true;
    for (const flowName of names) {
      const key = `${environment}/${flowName}`;
      const existing = this.queryOne('SELECT json FROM flows WHERE key = ? AND user = ?', [key, this.userOid]);
      if (!existing) continue;
      const stored = this.deserializeJson(existing.json as string) || {};
      stored.runScanQueuedAt = null;
      stored.runScanReason = null;
      stored.runScanAttempts = 0;
      stored.runScanLastError = null;
      this.exec(
        `UPDATE flows
            SET json = ?, runScanQueuedAt = NULL, runScanReason = NULL, runScanAttempts = 0, runScanLastError = NULL
          WHERE key = ? AND user = ?`,
        [JSON.stringify(stored), key, this.userOid]
      );
    }
    await this.persist();
    await this.refreshFlowSubjects();
    return true;
  }

  getRuns$(environment: string | null = null): Observable<FlowRun[]> {
    this.refreshRunsSubject(environment).catch((error) => {
      console.warn('[StorageOpfsSqliteService] getRuns$ initial refresh failed', error);
    });
    return this.runsSubject.asObservable();
  }

  async putRuns(runs: FlowRun[]): Promise<any> {
    await this.ensureReady();
    if (!runs?.length) {
      return true;
    }

    for (const run of runs) {
      const key = `${run.environment || ''}/${run.flowName || ''}/${run.name || ''}`;
      const environment = run.environment || '';
      const flowName = run.flowName || '';
      const startTime = run.startTime ? new Date(run.startTime as any).getTime() : null;
      const user = this.userOid;

      this.exec(
        'INSERT INTO runs (key, json, environment, flowName, user, startTime) VALUES (?, ?, ?, ?, ?, ?) ON CONFLICT(key) DO UPDATE SET json=excluded.json, environment=excluded.environment, flowName=excluded.flowName, user=excluded.user, startTime=excluded.startTime',
        [
          key,
          JSON.stringify(run),
          environment,
          flowName,
          user,
          startTime
        ]
      );
    }

    await this.persist();
    await this.refreshRunsSubject(null);
    return true;
  }

  async getUsers(): Promise<UserItem[]> {
    await this.ensureReady();
    const rows = this.queryAll('SELECT json FROM users WHERE user = ?', [this.userOid]);
    const users = rows.map((row) => this.deserializeJson(row.json as string) as UserItem);
    this.usersSubject.next(users);
    return users;
  }

  getUsers$(): Observable<UserItem[]> {
    this.getUsers().catch((error) => {
      console.warn('[StorageOpfsSqliteService] getUsers$ initial refresh failed', error);
    });
    return this.usersSubject.asObservable();
  }

  async putUsers(users: UserItem[]): Promise<any> {
    await this.ensureReady();
    if (!users?.length) {
      return true;
    }

    for (const user of users) {
      // Merge with the existing row's emails so aliases ACCUMULATE across writes.
      // Empty incoming fields (displayName/email) fall back to whatever is already
      // stored, so a placeholder write (e.g. flow-owner pre-population with just an
      // OID and no email yet) doesn't clobber a richer record from a later
      // enrichment pass — and a richer record won't clobber an even-richer one.
      const existingRow = this.queryOne(
        'SELECT json FROM users WHERE user = ? AND key = ?',
        [this.userOid, user.key]
      );
      const existing = existingRow ? (this.deserializeJson(existingRow.json as string) as UserItem | null) : null;
      const merged = mergeEmails(
        [...(existing?.emails ?? []), ...(user.emails ?? [])],
        user.email ?? ''
      );
      const normalised: UserItem = {
        ...existing,
        ...user,
        // Truthy-prefer: non-empty new value wins; empty new value falls back to existing.
        displayName: user.displayName || existing?.displayName || '',
        email: user.email || existing?.email || '',
        emails: merged,
      };
      this.exec(
        'INSERT INTO users (key, user, json, displayName, email) VALUES (?, ?, ?, ?, ?) ON CONFLICT(user, key) DO UPDATE SET json=excluded.json, displayName=excluded.displayName, email=excluded.email',
        [
          normalised.key,
          this.userOid,
          JSON.stringify(normalised),
          normalised.displayName || '',
          normalised.email || ''
        ]
      );
      for (const alias of merged) {
        this.exec(
          'INSERT INTO user_aliases (email, user, user_key) VALUES (?, ?, ?) ON CONFLICT(user, email) DO UPDATE SET user_key=excluded.user_key',
          [alias, this.userOid, normalised.key]
        );
      }
    }

    await this.persist();
    await this.refreshUsersSubject();
    return true;
  }

  async getUserByEmail(email: string): Promise<UserItem | undefined> {
    await this.ensureReady();
    const alias = this.queryOne('SELECT user_key FROM user_aliases WHERE user = ? AND email = ?', [this.userOid, email]);
    if (!alias?.user_key) return undefined;
    const row = this.queryOne('SELECT json FROM users WHERE user = ? AND key = ?', [this.userOid, alias.user_key as string]);
    return row ? (this.deserializeJson(row.json as string) as UserItem) : undefined;
  }

  async getEnvironment(name: string): Promise<StoredEnvironment | null> {
    await this.ensureReady();
    const row = this.queryOne(
      'SELECT name, displayName, json, scanCompletedAt, flowCount, durationMs, flowsAdminScannedAt, connectionsScannedAt, connectionsAdminScannedAt FROM environments WHERE name = ? AND user = ?',
      [name, this.userOid]
    );
    return row ? this.toStoredEnvironment(row) : null;
  }

  async getEnvironments(): Promise<StoredEnvironment[]> {
    await this.ensureReady();
    const rows = this.queryAll(
      'SELECT name, displayName, json, scanCompletedAt, flowCount, durationMs, flowsAdminScannedAt, connectionsScannedAt, connectionsAdminScannedAt FROM environments WHERE user = ?',
      [this.userOid]
    );
    return rows.map((row) => this.toStoredEnvironment(row));
  }

  async putEnvironment(env: any): Promise<any> {
    await this.putEnvironments([env]);
    return true;
  }

  async putEnvironments(envs: any[]): Promise<any> {
    await this.ensureReady();
    const list = (envs || []).filter((e) => e && e.name);
    if (!list.length) return true;
    // Identity/metadata only — do NOT touch scan columns so a prior completion
    // timestamp survives a re-discovery of the environment listing.
    for (const env of list) {
      const name = String(env.name);
      const displayName = String(env?.properties?.displayName ?? env?.displayName ?? '');
      this.exec(
        `INSERT INTO environments (name, displayName, user, json) VALUES (?, ?, ?, ?)
         ON CONFLICT(name) DO UPDATE SET
           displayName=excluded.displayName,
           user=excluded.user,
           json=excluded.json`,
        [name, displayName, this.userOid, JSON.stringify(env)]
      );
    }
    await this.persist();
    return true;
  }

  async markEnvScanCompleted(name: string, flowCount: number, durationMs: number): Promise<any> {
    await this.ensureReady();
    if (!name) return true;
    // Update only the scan columns; leave identity/json untouched.
    this.exec(
      `UPDATE environments
         SET scanCompletedAt = ?, flowCount = ?, durationMs = ?
       WHERE name = ? AND user = ?`,
      [Date.now(), flowCount ?? null, durationMs ?? null, name, this.userOid]
    );
    await this.persist();
    return true;
  }

  /**
   * Record a per-source scan completion. Maps each source to its dedicated
   * timestamp column so the header reload-age can show the correct freshness
   * for the page the user is on.
   *
   * `flows`           → scanCompletedAt (also writes flowCount + durationMs for backwards compat)
   * `flowsAdmin`      → flowsAdminScannedAt
   * `connections`     → connectionsScannedAt
   * `connectionsAdmin`→ connectionsAdminScannedAt
   */
  async markEnvSourceScanned(
    name: string,
    source: EnvScanSource,
    count?: number,
    durationMs?: number
  ): Promise<any> {
    await this.ensureReady();
    if (!name) return true;
    const now = Date.now();

    if (source === 'flows') {
      // Preserves the existing flow-scan column writes (flowCount + durationMs).
      this.exec(
        `UPDATE environments
           SET scanCompletedAt = ?, flowCount = ?, durationMs = ?
         WHERE name = ? AND user = ?`,
        [now, count ?? null, durationMs ?? null, name, this.userOid]
      );
    } else {
      const column = source === 'flowsAdmin'
        ? 'flowsAdminScannedAt'
        : source === 'connections'
          ? 'connectionsScannedAt'
          : 'connectionsAdminScannedAt';
      this.exec(
        `UPDATE environments SET ${column} = ? WHERE name = ? AND user = ?`,
        [now, name, this.userOid]
      );
    }

    await this.persist();
    return true;
  }

  private async ensureReady(): Promise<void> {
    if (this.db) {
      return;
    }

    if (this.dbInitPromise) {
      await this.dbInitPromise;
      return;
    }

    this.dbInitPromise = this.initDb();
    await this.dbInitPromise;
  }

  private async initDb(): Promise<void> {
    console.log('[opfs] initDb start');
    if (!('storage' in navigator) || !navigator.storage?.getDirectory) {
      throw new Error('OPFS is not available in this browser context.');
    }

    console.log('[opfs] loading sql.js wasm');
    this.sqlite = await initSqlJs({
      locateFile: locateSqliteWasm,
    });

    console.log('[opfs] wasm loaded, opening OPFS file');
    const root = await navigator.storage.getDirectory();
    this.fileHandle = await root.getFileHandle(FLOW_STUDIO_SQLITE_DB_FILE, { create: true });
    const file = await this.fileHandle.getFile();
    console.log('[opfs] reading DB file, size=', file.size);
    const bytes = new Uint8Array(await file.arrayBuffer());
    console.log('[opfs] DB bytes loaded, initialising SQLite');
    this.db = bytes.length > 0 ? new this.sqlite.Database(bytes) : new this.sqlite.Database();
    console.log('[opfs] SQLite ready, running schema');
    this.execSchema();
    console.log('[opfs] schema done, refreshing subjects');
    await this.refreshSubjects();
    console.log('[opfs] initDb complete');
  }

  private execSchema(): void {
    if (!this.db) return;

    this.db.exec(`
      CREATE TABLE IF NOT EXISTS configs (
        key TEXT NOT NULL,
        user TEXT NOT NULL,
        value TEXT,
        PRIMARY KEY(user, key)
      );

      CREATE TABLE IF NOT EXISTS flows (
        key TEXT PRIMARY KEY,
        json TEXT NOT NULL,
        environment TEXT,
        displayName TEXT,
        user TEXT,
        sources TEXT,
        runScanQueuedAt INTEGER,
        runScanReason TEXT,
        runScanAttempts INTEGER DEFAULT 0,
        runScanLastError TEXT
      );

      CREATE TABLE IF NOT EXISTS runs (
        key TEXT PRIMARY KEY,
        json TEXT NOT NULL,
        environment TEXT,
        flowName TEXT,
        user TEXT,
        startTime INTEGER
      );

      CREATE TABLE IF NOT EXISTS users (
        key TEXT NOT NULL,
        user TEXT NOT NULL,
        json TEXT NOT NULL,
        displayName TEXT,
        email TEXT,
        PRIMARY KEY(user, key)
      );

      CREATE TABLE IF NOT EXISTS user_aliases (
        email TEXT NOT NULL,
        user TEXT NOT NULL,
        user_key TEXT NOT NULL,
        PRIMARY KEY(user, email)
      );

      CREATE TABLE IF NOT EXISTS environments (
        name TEXT PRIMARY KEY,
        displayName TEXT,
        user TEXT,
        json TEXT,
        scanCompletedAt INTEGER,
        flowCount INTEGER,
        durationMs INTEGER,
        flowsAdminScannedAt INTEGER,
        connectionsScannedAt INTEGER,
        connectionsAdminScannedAt INTEGER
      );

      CREATE TABLE IF NOT EXISTS connections (
        key TEXT NOT NULL,
        user TEXT NOT NULL,
        json TEXT NOT NULL,
        environment TEXT,
        sources TEXT,
        apiId TEXT,
        displayName TEXT,
        lastModifiedAt INTEGER,
        PRIMARY KEY(user, key)
      );

      CREATE INDEX IF NOT EXISTS idx_flows_user_env ON flows(user, environment);
      CREATE INDEX IF NOT EXISTS idx_runs_user_env ON runs(user, environment);
      CREATE INDEX IF NOT EXISTS idx_environments_user ON environments(user);
      CREATE INDEX IF NOT EXISTS idx_connections_user_env ON connections(user, environment);
    `);

    this.migrateUserScopedTables();
    this.migrateFlowsTable();
    this.migrateEnvironmentsTable();
    this.migrateConnectionsTable();
    this.dropLegacyFlowDefinitionsTable();

    this.db.exec(`
      CREATE INDEX IF NOT EXISTS idx_configs_user ON configs(user);
      CREATE INDEX IF NOT EXISTS idx_users_user ON users(user);
      CREATE INDEX IF NOT EXISTS idx_user_aliases_user_email ON user_aliases(user, email);
    `);
  }

  /**
   * One-time cleanup: drop the legacy `flow_definitions` table. The full-text
   * search index it backed has been removed in favour of metadata-only search
   * over the `flows` table. Idempotent.
   */
  private dropLegacyFlowDefinitionsTable(): void {
    this.db.exec(`DROP TABLE IF EXISTS flow_definitions;`);
  }

  private migrateUserScopedTables(): void {
    this.migrateConfigsTable();
    this.migrateUsersTable();
    this.migrateUserAliasesTable();
  }

  /**
   * One-time migration: collapse the legacy `flows_admin` table into the
   * unified `flows` table and add the `sources` column. Each merged row carries
   * a deduped, comma-separated list of origin tags ('my', 'shared', 'admin', 'graphql').
   *
   * Idempotent: if `flows_admin` is already gone and `flows.sources` exists,
   * nothing happens.
   */
  private migrateFlowsTable(): void {
    if (!this.hasColumn('flows', 'sources')) {
      this.db.exec(`ALTER TABLE flows ADD COLUMN sources TEXT;`);
    }
    if (!this.hasColumn('flows', 'runScanQueuedAt')) {
      this.db.exec(`ALTER TABLE flows ADD COLUMN runScanQueuedAt INTEGER;`);
    }
    if (!this.hasColumn('flows', 'runScanReason')) {
      this.db.exec(`ALTER TABLE flows ADD COLUMN runScanReason TEXT;`);
    }
    if (!this.hasColumn('flows', 'runScanAttempts')) {
      this.db.exec(`ALTER TABLE flows ADD COLUMN runScanAttempts INTEGER DEFAULT 0;`);
    }
    if (!this.hasColumn('flows', 'runScanLastError')) {
      this.db.exec(`ALTER TABLE flows ADD COLUMN runScanLastError TEXT;`);
    }

    const flowsAdminExists = this.queryAll(
      "SELECT name FROM sqlite_master WHERE type='table' AND name='flows_admin'", []
    ).length > 0;
    if (!flowsAdminExists) return;

    const adminHasAdminCol = this.hasColumn('flows_admin', 'admin');
    const adminRows = this.queryAll(
      adminHasAdminCol
        ? 'SELECT key, json, environment, displayName, admin FROM flows_admin'
        : 'SELECT key, json, environment, displayName FROM flows_admin',
      []
    );

    for (const adminRow of adminRows) {
      const key = String(adminRow.key ?? '');
      if (!key) continue;
      const owner = String((adminRow as any).admin ?? this.userOid ?? '');
      const adminStored = this.deserializeJson(adminRow.json as string) || {};

      const existing = this.queryOne(
        'SELECT json, sources, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError FROM flows WHERE key = ? AND user = ?',
        [key, owner]
      );
      const existingStored = existing ? (this.deserializeJson(existing.json as string) || {}) : {};
      const merged = mergeSources(
        normalizeSources(existingStored.sources),
        mergeSources(normalizeSources(adminStored.sources), ['admin'])
      );

      const stored = existing ? { ...existingStored } : { ...adminStored };
      stored.sources = merged;
      this.applyExistingRunScanQueue(stored, existing, existingStored);
      const sourcesCsv = merged.join(',').toLowerCase();
      const env = String(adminRow.environment ?? existingStored.environment ?? adminStored.environment ?? '');
      const displayName = String(adminRow.displayName ?? existingStored.displayName ?? adminStored.displayName ?? '');

      this.exec(
        `INSERT INTO flows (key, json, environment, displayName, user, sources, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
         ON CONFLICT(key) DO UPDATE SET
           json=excluded.json,
           environment=excluded.environment,
           displayName=excluded.displayName,
           user=excluded.user,
           sources=excluded.sources,
           runScanQueuedAt=excluded.runScanQueuedAt,
           runScanReason=excluded.runScanReason,
           runScanAttempts=excluded.runScanAttempts,
           runScanLastError=excluded.runScanLastError`,
        [key, JSON.stringify(stored), env, displayName, owner, sourcesCsv, stored.runScanQueuedAt ?? null, stored.runScanReason ?? null, stored.runScanAttempts ?? 0, stored.runScanLastError ?? null]
      );
    }

    this.db.exec(`DROP TABLE IF EXISTS flows_admin;`);
  }

  /**
   * Replace the legacy `scope` column with a multi-value `sources` CSV — same
   * pattern as flows. A connection visible in both user and admin scope keeps
   * a single row tagged `sources='user,admin'` instead of being clobbered by
   * whichever scope wrote last.
   *
   * Idempotent: bails when `sources` already exists. The legacy `scope` column
   * is left in place if SQLite can't drop it; no harm — nothing reads it.
   */
  private migrateConnectionsTable(): void {
    if (!this.hasColumn('connections', 'sources')) {
      this.db.exec(`ALTER TABLE connections ADD COLUMN sources TEXT;`);
    }
    if (this.hasColumn('connections', 'scope')) {
      // Carry forward whatever single-value scope each existing row had.
      this.db.exec(`UPDATE connections SET sources = scope WHERE sources IS NULL OR sources = '';`);
      try {
        this.db.exec(`ALTER TABLE connections DROP COLUMN scope;`);
      } catch {
        /* older SQLite — leave the orphan column in place */
      }
    }
  }

  /**
   * Add per-source scan-timestamp columns to existing `environments` rows.
   * Idempotent — bails out once the columns are present.
   */
  private migrateEnvironmentsTable(): void {
    if (!this.hasColumn('environments', 'flowsAdminScannedAt')) {
      this.db.exec(`ALTER TABLE environments ADD COLUMN flowsAdminScannedAt INTEGER;`);
    }
    if (!this.hasColumn('environments', 'connectionsScannedAt')) {
      this.db.exec(`ALTER TABLE environments ADD COLUMN connectionsScannedAt INTEGER;`);
    }
    if (!this.hasColumn('environments', 'connectionsAdminScannedAt')) {
      this.db.exec(`ALTER TABLE environments ADD COLUMN connectionsAdminScannedAt INTEGER;`);
    }
  }

  private migrateConfigsTable(): void {
    if (this.hasColumn('configs', 'user')) return;

    this.db.exec(`
      ALTER TABLE configs RENAME TO configs_legacy;
      CREATE TABLE configs (
        key TEXT NOT NULL,
        user TEXT NOT NULL,
        value TEXT,
        PRIMARY KEY(user, key)
      );
      INSERT INTO configs (key, user, value)
        SELECT key, '${this.escapeSql(this.userOid)}', value FROM configs_legacy;
      DROP TABLE configs_legacy;
      CREATE INDEX IF NOT EXISTS idx_configs_user ON configs(user);
    `);
  }

  private migrateUsersTable(): void {
    if (this.hasColumn('users', 'user')) return;

    this.db.exec(`
      ALTER TABLE users RENAME TO users_legacy;
      CREATE TABLE users (
        key TEXT NOT NULL,
        user TEXT NOT NULL,
        json TEXT NOT NULL,
        displayName TEXT,
        email TEXT,
        PRIMARY KEY(user, key)
      );
      INSERT INTO users (key, user, json, displayName, email)
        SELECT key, '${this.escapeSql(this.userOid)}', json, displayName, email FROM users_legacy;
      DROP TABLE users_legacy;
      CREATE INDEX IF NOT EXISTS idx_users_user ON users(user);
    `);
  }

  private migrateUserAliasesTable(): void {
    if (this.hasColumn('user_aliases', 'user')) return;

    this.db.exec(`
      ALTER TABLE user_aliases RENAME TO user_aliases_legacy;
      CREATE TABLE user_aliases (
        email TEXT NOT NULL,
        user TEXT NOT NULL,
        user_key TEXT NOT NULL,
        PRIMARY KEY(user, email)
      );
      INSERT INTO user_aliases (email, user, user_key)
        SELECT email, '${this.escapeSql(this.userOid)}', user_key FROM user_aliases_legacy;
      DROP TABLE user_aliases_legacy;
      CREATE INDEX IF NOT EXISTS idx_user_aliases_user_email ON user_aliases(user, email);
    `);
  }

  private hasColumn(table: string, column: string): boolean {
    return this.queryAll(`PRAGMA table_info(${table})`, [])
      .some((row) => String(row.name || '').toLowerCase() === column.toLowerCase());
  }

  private escapeSql(value: string): string {
    return `${value || ''}`.replace(/'/g, "''");
  }

  private exec(sql: string, params: SqlValue[]): void {
    if (!this.db) throw new Error('OPFS SQLite DB is not initialized.');
    const stmt = this.db.prepare(sql);
    try {
      stmt.bind(params);
      stmt.step();
    } finally {
      stmt.free();
    }
  }

  private queryAll(sql: string, params: SqlValue[]): Row[] {
    if (!this.db) throw new Error('OPFS SQLite DB is not initialized.');
    const stmt = this.db.prepare(sql);
    const rows: Row[] = [];
    try {
      stmt.bind(params);
      while (stmt.step()) {
        rows.push(stmt.getAsObject() as Row);
      }
    } finally {
      stmt.free();
    }
    return rows;
  }

  private queryOne(sql: string, params: SqlValue[]): Row | null {
    const rows = this.queryAll(sql, params);
    return rows.length > 0 ? rows[0] : null;
  }

  private async persist(): Promise<void> {
    if (!this.db || !this.fileHandle) return;
    const bytes = this.db.export();
    const writable = await this.fileHandle.createWritable();
    await writable.write(bytes);
    await writable.close();
  }

  private deserializeJson(value: string): any {
    try {
      return JSON.parse(value || 'null');
    } catch {
      return null;
    }
  }

  private toFlow(json: string): Flow {
    const raw = this.deserializeJson(json) || {};
    const flow = Object.assign(new Flow(), raw);
    flow.runs = flow.runs || [];
    flow.sharingType = flow.sharingType || '';
    flow.sources = normalizeSources(raw.sources ?? raw.source);
    if (raw.modified) { flow.modified = new Date(raw.modified); }
    if (raw.scanned) { flow.scanned = new Date(raw.scanned); }
    return flow;
  }

  private toFlowSummary(row: Row): Flow {
    const raw = this.deserializeJson(row.json as string) || {};
    const environment = (row.environment as string) || '';
    const key = (row.key as string) || '';
    const flowName = environment && key.startsWith(`${environment}/`)
      ? key.substring(environment.length + 1)
      : key.split('/').pop() || key;
    const flow = Object.assign(new Flow(), {
      _id: flowName,
      flowName,
      displayName: (row.displayName as string) || flowName,
      environment,
      sources: normalizeSources(raw.sources ?? raw.source),
      backgroundColor: raw.backgroundColor,
      description: raw.description || null,
      flowState: raw.flowState || '',
      modified: raw.modified ? new Date(raw.modified as any) : null,
      triggers: raw.triggers || [],
      owners: raw.owners || [],
      sharingType: raw.sharingType || '',
      userType: raw.userType || '',
      actionsCount: raw.actionsCount || 0,
      actionsTooltip: raw.actionsTooltip || '',
      solution: raw.solution || null,
      flowSuspensionReason: raw.flowSuspensionReason || '',
      monitored: raw.monitored,
      runScanQueuedAt: Number(row.runScanQueuedAt ?? raw.runScanQueuedAt ?? 0) || null,
      runScanReason: String(row.runScanReason ?? raw.runScanReason ?? '') || null,
      runScanAttempts: Number(row.runScanAttempts ?? raw.runScanAttempts ?? 0) || 0,
      runScanLastError: String(row.runScanLastError ?? raw.runScanLastError ?? '') || null,
      scanned: raw.scanned ? new Date(raw.scanned as any) : null,
      runs: []
    });
    return flow;
  }

  private toStoredFlow(flow: Flow): any {
    const { flowName, displayName, environment, backgroundColor, flowState, modified, triggers, userType, sharingType, owners, actionsCount, actionsTooltip, description, scanned, solution, flowSuspensionReason, monitored, sources, runScanQueuedAt, runScanReason, runScanAttempts, runScanLastError } = flow;
    return {
      key: `${environment}/${flowName}`,
      flowName,
      displayName,
      environment,
      sources: normalizeSources(sources),
      backgroundColor,
      description,
      flowState,
      modified,
      triggers: (triggers || []).map((trigger) => {
        const { icon, ...rest } = trigger;
        return {
          icon: (icon && /^data:/gi.test(icon)) ? null : icon,
          ...rest
        };
      }),
      userType,
      sharingType,
      owners,
      actionsCount,
      actionsTooltip,
      solution,
      flowSuspensionReason,
      monitored,
      runScanQueuedAt: Number(runScanQueuedAt || 0) || null,
      runScanReason: runScanReason || null,
      runScanAttempts: Number(runScanAttempts || 0) || 0,
      runScanLastError: runScanLastError || null,
      scanned
    };
  }

  private applyExistingRunScanQueue(stored: any, existing: Row | null, existingStored: any): void {
    if (stored.runScanQueuedAt) return;
    stored.runScanQueuedAt = Number(existing?.runScanQueuedAt ?? existingStored?.runScanQueuedAt ?? 0) || null;
    stored.runScanReason = String(existing?.runScanReason ?? existingStored?.runScanReason ?? '') || null;
    stored.runScanAttempts = Number(existing?.runScanAttempts ?? existingStored?.runScanAttempts ?? 0) || 0;
    stored.runScanLastError = String(existing?.runScanLastError ?? existingStored?.runScanLastError ?? '') || null;
  }

  private toRun(json: string): FlowRun {
    const raw = this.deserializeJson(json) || {};
    const run = Object.assign(new FlowRun(), raw);
    if (raw.startTime) { run.startTime = new Date(raw.startTime); }
    if (raw.endTime) { run.endTime = new Date(raw.endTime); }
    return run;
  }

  private toStoredEnvironment(row: Row): StoredEnvironment {
    return {
      name: String(row.name ?? ''),
      displayName: String(row.displayName ?? ''),
      json: this.deserializeJson(row.json as string),
      scanCompletedAt: row.scanCompletedAt == null ? null : Number(row.scanCompletedAt),
      flowCount: row.flowCount == null ? null : Number(row.flowCount),
      durationMs: row.durationMs == null ? null : Number(row.durationMs),
      flowsAdminScannedAt: row.flowsAdminScannedAt == null ? null : Number(row.flowsAdminScannedAt),
      connectionsScannedAt: row.connectionsScannedAt == null ? null : Number(row.connectionsScannedAt),
      connectionsAdminScannedAt: row.connectionsAdminScannedAt == null ? null : Number(row.connectionsAdminScannedAt),
    };
  }

  // -----------------------------------------------------------------------
  // Connections cache
  // -----------------------------------------------------------------------

  /**
   * Read cached connections, optionally filtered by environment. Always pushes
   * the fresh result through `connectionsSubject` so subscribers re-render
   * after every read.
   */
  async getConnections(environment?: string): Promise<Connection[]> {
    await this.ensureReady();
    const rows = environment
      ? this.queryAll(
          'SELECT json FROM connections WHERE user = ? AND environment = ?',
          [this.userOid, environment]
        )
      : this.queryAll('SELECT json FROM connections WHERE user = ?', [this.userOid]);
    const connections = rows.map((row) => this.toConnection(row.json as string));
    this.connectionsSubject.next(connections);
    return connections;
  }

  getConnections$(): Observable<Connection[]> {
    this.getConnections().catch((error) => {
      console.warn('[StorageOpfsSqliteService] getConnections$ initial refresh failed', error);
    });
    return this.connectionsSubject.asObservable();
  }

  /**
   * Merge connections from a single scope's fetch into the cache.
   *
   * `scope` is the origin tag for THIS fetch ('user' or 'admin'). The stored
   * row carries a multi-value `sources` CSV — same pattern as `Flow.sources`
   * — so a connection visible in both scopes lives as a single row tagged
   * `sources='user,admin'` rather than being clobbered by whichever scope
   * wrote last.
   *
   * Two-phase update:
   *   1. Drop the tag from rows previously tagged with this scope but absent
   *      from the current fetch. If no other source tags remain, delete the row.
   *   2. Upsert each incoming connection with sources = union(existing, [scope]).
   */
  async putConnections(connections: Connection[], environment: string, scope: 'user' | 'admin'): Promise<any> {
    await this.ensureReady();
    if (!environment) return true;

    const incomingKeys = new Set<string>();
    for (const c of connections || []) {
      const key = c?.id || c?.name;
      if (key) incomingKeys.add(key);
    }

    // Phase 1: connections previously tagged with `scope` that aren't in the
    // fresh fetch. Drop just the tag; if no scopes remain, drop the row.
    const existingRows = this.queryAll(
      'SELECT key, sources FROM connections WHERE user = ? AND environment = ?',
      [this.userOid, environment]
    );
    for (const row of existingRows) {
      const key = String(row.key ?? '');
      if (!key || incomingKeys.has(key)) continue;
      const sources = normalizeSources(row.sources);
      if (!sources.includes(scope)) continue;
      const reduced = sources.filter((s) => s !== scope);
      if (reduced.length === 0) {
        this.exec('DELETE FROM connections WHERE user = ? AND key = ?', [this.userOid, key]);
      } else {
        this.exec(
          'UPDATE connections SET sources = ? WHERE user = ? AND key = ?',
          [JSON.stringify(reduced.map(s => s.toLowerCase())), this.userOid, key]
        );
      }
    }

    // Phase 2: upsert incoming connections, merging sources with whatever's
    // already stored so cross-scope visibility accumulates.
    for (const c of connections || []) {
      const key = c?.id || c?.name;
      if (!key) continue;
      const existing = this.queryOne(
        'SELECT sources FROM connections WHERE user = ? AND key = ?',
        [this.userOid, key]
      );
      const existingSources = existing ? normalizeSources(existing.sources as string) : [];
      const merged = mergeSources(existingSources, [scope]);

      // Mirror the in-memory connection so any subscriber observes the union.
      c.sources = merged;

      const stored = this.toStoredConnection(c);
      // JSON-array form, same as flows.sources. normalizeSources reads both
      // shapes back, so the rolling migration is invisible to consumers.
      const sourcesJson = JSON.stringify(merged.map(s => s.toLowerCase()));
      const lastModifiedAt = c.lastModifiedTime ? c.lastModifiedTime.getTime() : 0;
      this.exec(
        'INSERT INTO connections (key, user, json, environment, sources, apiId, displayName, lastModifiedAt) VALUES (?, ?, ?, ?, ?, ?, ?, ?) ' +
        'ON CONFLICT(user, key) DO UPDATE SET ' +
        '  json=excluded.json,' +
        '  environment=excluded.environment,' +
        '  sources=excluded.sources,' +
        '  apiId=excluded.apiId,' +
        '  displayName=excluded.displayName,' +
        '  lastModifiedAt=excluded.lastModifiedAt',
        [
          key,
          this.userOid,
          JSON.stringify(stored),
          environment,
          sourcesJson,
          c.apiId || '',
          c.displayName || '',
          lastModifiedAt,
        ]
      );
    }

    await this.persist();
    await this.refreshConnectionsSubject();
    return true;
  }

  /**
   * Serializable subset of a Connection. Mirrors the field shape the
   * Connection constructor expects (`name`, `id`, `properties.*`) so we can
   * round-trip through `new Connection(stored)` without a separate hydrator.
   * Sources travel as a top-level `_sources` array (kept off `properties` so
   * it never collides with anything an upstream payload might carry).
   */
  private toStoredConnection(c: Connection): any {
    return {
      name: c.name,
      id: c.id,
      _sources: Array.isArray(c.sources) ? c.sources : [],
      properties: {
        apiId: c.apiId,
        displayName: c.displayName,
        iconUri: c.iconUri,
        // Connection.initialize joins statuses with ', '; preserve via a single status entry.
        statuses: c.status ? [{ status: c.status }] : [],
        createdTime: c.createdTime ? c.createdTime.toISOString() : null,
        lastModifiedTime: c.lastModifiedTime ? c.lastModifiedTime.toISOString() : null,
        environment: { name: c.environment || '' },
        metadata: c.metadata || {},
      },
    };
  }

  private toConnection(json: string): Connection {
    return new Connection(this.deserializeJson(json));
  }

  private async refreshConnectionsSubject(): Promise<void> {
    this.connectionsSubject.next(await this.getConnections());
  }

  private async refreshFlowSubjects(): Promise<void> {
    this.flowsSubject.next(await this.getFlows());
    this.flowsAdminSubject.next(await this.getFlowsAdmin());
  }

  private async refreshRunsSubject(environment: string | null): Promise<void> {
    await this.ensureReady();
    const rows = this.queryAll(
      'SELECT json FROM runs WHERE user = ? AND (? IS NULL OR environment = ?) ORDER BY startTime DESC NULLS LAST',
      [this.userOid, environment, environment]
    );
    this.runsSubject.next(rows.map((row) => this.toRun(row.json as string)));
  }

  private async refreshUsersSubject(): Promise<void> {
    this.usersSubject.next(await this.getUsers());
  }

  private async refreshSubjects(): Promise<void> {
    console.log('[opfs] refreshSubjects: flows');
    await this.refreshFlowSubjects();
    console.log('[opfs] refreshSubjects: runs');
    await this.refreshRunsSubject(null);
    console.log('[opfs] refreshSubjects: users');
    await this.refreshUsersSubject();
    console.log('[opfs] refreshSubjects done');
  }
}
